// Eskom PARCC — Findings-register CSV builder (pure).
//
// The monolith's `exportCsv` (index.html) rebuilt from CLEAN typed data: it maps
// each finding to its transaction (by `referenceNo`) and emits the fifteen
// register columns. Every value is wrapped in double quotes with embedded quotes
// doubled (RFC 4180), exactly as the monolith did — so commas, quotes and
// newlines inside a value never corrupt the row. No DOM, no Blob: the download
// glue (downloadText) lives separately so this is unit-tested as a string.
import type { Transaction } from '../../data/types';
import type { FindingRead } from '../../lib/db';
import type { RuleReferences } from '../../domain/rulebook/ruleReferences';
import { splitFindingDisplay } from '../findings/findingDisplay';
import { deriveFindingReviewedState } from '../findings/filterFindings';
import { normalizeFindingStatus } from '../../domain/normalize';
import { neutraliseCell } from './neutraliseCell';

/** Map from a transaction `referenceNo` to its transaction (the finding join). */
export type TxByRef = ReadonlyMap<string, Transaction>;

/**
 * The register columns, in the monolith's order, plus the rulebook regulatory
 * references appended (item 1). British-English headers. Shared by the CSV and
 * the XLSX builders so the column model lives in ONE place.
 */
export const FINDINGS_CSV_HEADERS = [
  'Dataset',
  'Reference',
  'Year',
  'Division',
  'Period',
  'Supplier',
  'Procedure',
  'Finding',
  'Status',
  'Reviewed',
  'Route',
  'Confidence',
  'Matched Rule Theme',
  'Anchors',
  'Compliance',
  'Rationale',
  'Notes',
  'Regulatory References',
  // AI provenance — now READ from the finding (previously write-only). "PFMA
  // Impact" is the engine's aiPfmaSection, the column the deliverable expects;
  // "AI-assisted" marks a finding that came from a confirmed engine proposal, so
  // its Confidence is the engine's; "AI Model" attributes which model produced it.
  'PFMA Impact',
  'AI-assisted',
  'AI Model',
] as const;

/** Flatten the rulebook references into one cell: "Eskom: …; PFMA: …; Law: …". */
function flattenRefs(refs: RuleReferences | undefined): string {
  if (!refs) return '';
  return [
    refs.eskomPolicy?.length ? `Eskom: ${refs.eskomPolicy.join('; ')}` : '',
    refs.pfma?.length ? `PFMA: ${refs.pfma.join('; ')}` : '',
    refs.law?.length ? `Law: ${refs.law.join('; ')}` : '',
  ]
    .filter(Boolean)
    .join(' | ');
}

/** The finding's lifecycle status, folded onto the four-state vocabulary
 *  (Open / In Progress / Closed / Closed by Self-Declaration) — same rule as
 *  the on-screen register. */
function findingStatus(f: FindingRead): string {
  return normalizeFindingStatus(f.effectiveStatus ?? f.legacyStatus ?? '');
}

/** The compliance level shown/exported: the final result, else the suggestion. */
function findingCompliance(f: FindingRead): string {
  return f.finalCompliance ?? f.suggestedCompliance ?? '';
}

/** Route, monolith-style: upper-cased (blank stays blank). */
function routeLabel(route: string | undefined): string {
  return (route ?? '').trim().toUpperCase();
}

/** RFC 4180 field: always quoted, embedded quotes doubled. Mirrors the monolith. */
function csvField(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

/**
 * Build the ordered cell values for one finding row, joined to its transaction.
 * Shared by the CSV and XLSX builders (one column model). Order matches
 * {@link FINDINGS_CSV_HEADERS}.
 */
export function findingRowCells(f: FindingRead, txByRef: TxByRef): string[] {
  const ref = f.referenceNo ?? '';
  const tx = ref ? txByRef.get(ref) : undefined;
  // R3 #2: the Finding column carries the reviewer's note; the procedure it was
  // captured against gets its own column (legacy criterion+note blobs split).
  const { procedure, note } = splitFindingDisplay(f);
  return [
    tx?.dataset ?? '',
    ref,
    tx?.year ?? '',
    tx?.division ?? '',
    tx?.period ?? '',
    tx?.supplier ?? '',
    procedure,
    note,
    findingStatus(f),
    deriveFindingReviewedState(tx),
    routeLabel(f.route),
    f.confidence ?? '',
    (f.matchedRules ?? []).join('; '),
    (f.anchors ?? []).join('; '),
    findingCompliance(f),
    f.rationale ?? '',
    f.reviewNotes ?? '',
    flattenRefs(f.regulatoryRefs),
    f.aiPfmaSection ?? '',
    f.isAiAssisted ? 'Yes' : '',
    f.aiModel ?? '',
    // Defence-in-depth: neutralise any cell that a spreadsheet could read as a formula.
  ].map(neutraliseCell);
}

/**
 * Build the findings-register CSV text from typed data. Pass the findings to
 * export (already filtered by the caller) and a `referenceNo → Transaction`
 * join map. Returns the full CSV string (header row + one row per finding);
 * empty findings yield a header-only document. Rows are LF-separated and every
 * field is quoted-and-escaped.
 */
export function buildFindingsCsv(findings: readonly FindingRead[], txByRef: TxByRef): string {
  const header = FINDINGS_CSV_HEADERS.map((h) => csvField(h)).join(',');
  const lines = findings.map((f) => findingRowCells(f, txByRef).map(csvField).join(','));
  return [header, ...lines].join('\n');
}
